import FontFamilyVariantDropdown from '@src/components/common/FontFamilyVariantDropdown';
import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import ArrowDown from '@src/svgs/ArrowDown';
import EllipsisIcon from '@src/svgs/EllipsisIcon';
import { FONT_SIZE_MAX, FONT_SIZE_MIN } from '@src/utility/general';
import { observer } from 'mobx-react-lite';
import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

import { TRANSPARENT_COLOR } from '../ColorDropdownSelect';
import ColorPicker from '../ColorPicker';
import { isBarTarget } from './barGate';
import { captionColorSlots } from './captionColorSlots';
import { useFloatingToolbarHost } from './FloatingToolbarHost';
import {
  FloatingToolbarSurface,
  ToolbarDivider,
  ToolbarIconButton,
  ToolbarMenu,
  ToolbarMenuItem,
  ToolbarTextButton,
} from './FloatingToolbarSurface';
import { ToolbarLayer } from './ToolbarLayer';
import { ToolbarStepper } from './ToolbarStepper';
import { useCaptionsBarController } from './useCaptionsBarController';
import { useFloatingToolbarPosition } from './useFloatingToolbarPosition';

import type { CaptionColorSlot } from './captionColorSlots';
import type { FloatingToolbarHost } from './FloatingToolbarHost';
import type { CaptionPositionSnap } from './useCaptionsBarController';

const POSITIONS: { snap: CaptionPositionSnap; label: string }[] = [
  { snap: 'top', label: 'Top' },
  { snap: 'middle', label: 'Middle' },
  { snap: 'bottom', label: 'Bottom' },
];

/**
 * PLAT-12100 — the captions bar: Style · Font · Size · Colour · Position ·
 * More, above the picture whenever a caption is selected (epic PLAT-12097,
 * DE1 / DE2 / DE5 / DE7 / DE8).
 *
 * It edits the caption settings, not the caption's text element, through the
 * same functions the Captions panel uses (`useCaptionsBarController`), so a
 * change in either shows in the other and every change is one undo step.
 * No Delete and no Hide: captions are a track, and hiding them would hide the
 * element this bar sits on (DS1) — turning them off stays in the panel.
 */
const CaptionsFloatingToolbar = observer(() => {
  // PLAT-12101 — the surface's host; no host, no bar.
  const host = useFloatingToolbarHost();
  const activeElement = host?.getActiveElement();
  // `track` arrives as a string from Creatomate state, hence `Number(...)`.
  const isCaption =
    !!host &&
    Number(activeElement?.track) === KARAOKE_TRACK_NUMBER &&
    isBarTarget(host);
  const elementId = activeElement?.source?.id;

  if (!host || !isCaption || !elementId) return null;
  // Keyed by caption so menus start closed on a new selection.
  return (
    <CaptionsToolbarInner key={elementId} host={host} elementId={elementId} />
  );
});

export default CaptionsFloatingToolbar;

const CaptionsToolbarInner = observer(
  ({ host, elementId }: { host: FloatingToolbarHost; elementId: string }) => {
    const { toolbarRef, pos } = useFloatingToolbarPosition(true, elementId);
    const bar = useCaptionsBarController(host, elementId);

    const [styleMenuPos, setStyleMenuPos] = useState<{
      top: number;
      left: number;
    } | null>(null);
    // The open colour picker: which caption colour, anchored where.
    const [colorPicker, setColorPicker] = useState<{
      property: CaptionColorSlot['property'];
      rect: DOMRect;
    } | null>(null);

    const shownSlotsRef = useRef(new Set<CaptionColorSlot['property']>());
    const styleTriggerRef = useRef<HTMLButtonElement>(null);
    const styleMenuRef = useRef<HTMLDivElement>(null);
    const closeStyleOnOutside = useCallback((e: MouseEvent) => {
      const trigger = styleTriggerRef.current;
      if (trigger && e.target instanceof Node && trigger.contains(e.target)) {
        return;
      }
      setStyleMenuPos(null);
    }, []);
    useOutsideAlerter(styleMenuRef, closeStyleOnOutside);

    if (!bar.config) return null;
    const { config } = bar;

    // Writers store px, but a config can still arrive in vh; show it in px.
    const fontSize = Math.round(
      host.textProducer.convertToPx(config.font_size, elementId) || 36,
    );
    // One button per colour the style uses (text, highlight, background,
    // outline), read from the config every render so the panel and undo show.
    // A button stays for this caption once shown, so an erased colour can be
    // picked again.
    const colorSlots = captionColorSlots(config, shownSlotsRef.current);
    colorSlots.forEach(slot => shownSlotsRef.current.add(slot.property));
    const pickingSlot = colorPicker
      ? colorSlots.find(slot => slot.property === colorPicker.property)
      : undefined;

    // More toggles Captions › Styles (DE5) through the host (PLAT-12101); a
    // surface with no panel gets no More.
    const { settings } = host;
    const isSidebarOnCaptions = !!settings?.isOpen('captions');

    const styleMenu =
      styleMenuPos && bar.templates.length > 0 ? (
        <SStyleMenu
          ref={styleMenuRef}
          style={{ top: styleMenuPos.top, left: styleMenuPos.left }}
          onMouseDown={e => e.stopPropagation()}
        >
          {bar.templates.map(template => (
            <ToolbarMenuItem
              key={template.templateId}
              isSelected={template.templateId === bar.templateId}
              onClick={() => {
                setStyleMenuPos(null);
                bar.applyTemplate(template);
              }}
            >
              {template.title}
            </ToolbarMenuItem>
          ))}
        </SStyleMenu>
      ) : null;

    // ColorPicker portals itself and computes its own placement, so it is safe
    // beside the bar (the surface's fade-in transform would trap a fixed child).
    const colorPickerPopover =
      colorPicker && pickingSlot ? (
        <ColorPicker
          pos={colorPicker.rect}
          defaultValue={pickingSlot.value}
          // A caption background is often see-through; the others start solid.
          solidDefaultAlpha={pickingSlot.property !== 'background_color'}
          getValue={color => {
            // Erase sends null; as in the Captions panel, that is no
            // background, or a transparent colour for the others.
            const erased =
              pickingSlot.property === 'background_color'
                ? null
                : TRANSPARENT_COLOR;
            bar.modify({ [pickingSlot.property]: color ?? erased });
          }}
          onClose={() => setColorPicker(null)}
        />
      ) : null;

    return (
      <ToolbarLayer>
        <FloatingToolbarSurface ref={toolbarRef} pos={pos}>
          {/* Style — the workspace's caption templates */}
          {/* Off where the surface picks the style elsewhere (PLAT-12102 DS4). */}
          {host.captions.offerStyle && bar.templates.length > 0 && (
            <>
              <SStyleButton
                ref={styleTriggerRef}
                title={bar.templateTitle}
                aria-label="Caption style"
                isActive={!!styleMenuPos}
                onClick={e => {
                  if (styleMenuPos) {
                    setStyleMenuPos(null);
                  } else {
                    const r = e.currentTarget.getBoundingClientRect();
                    setStyleMenuPos({ top: r.bottom + 6, left: r.left });
                  }
                }}
              >
                <SLabel>{bar.templateTitle}</SLabel>
                {/* The design system's dropdown chevron, as on the font picker. */}
                <ArrowDown width="10" height="10" strokeColor="var(--accent)" />
              </SStyleButton>
              <ToolbarDivider />
            </>
          )}

          {/* Font — the Captions panel's own picker */}
          <SFontPicker
            action={(_, value, variant) =>
              bar.setFont(value || undefined, variant)
            }
            propertyName="font_family"
            defaultValue={config.font_family}
            fontWeight={config.font_weight}
            menuWidth={240}
          />

          <ToolbarDivider />

          {/* Size */}
          <ToolbarStepper
            value={fontSize}
            min={FONT_SIZE_MIN}
            max={FONT_SIZE_MAX}
            step={1}
            onChange={next => bar.modify({ font_size: String(next) })}
            decreaseLabel="Decrease caption size"
            increaseLabel="Increase caption size"
          >
            {fontSize}
          </ToolbarStepper>

          <ToolbarDivider />

          {/* Colours — one per colour the caption style uses */}
          {colorSlots.map(slot => {
            const isOpen = colorPicker?.property === slot.property;
            return (
              <ToolbarIconButton
                key={slot.property}
                title={slot.label}
                aria-label={slot.label}
                isActive={isOpen}
                onClick={e => {
                  if (isOpen) {
                    setColorPicker(null);
                  } else {
                    setColorPicker({
                      property: slot.property,
                      rect: e.currentTarget.getBoundingClientRect(),
                    });
                  }
                }}
              >
                <SSwatch style={{ background: slot.value }} />
              </ToolbarIconButton>
            );
          })}

          <ToolbarDivider />

          {/* Position — the panel slider's Top, Center Point and Safe Zone */}
          {POSITIONS.map(({ snap, label }) => (
            <ToolbarTextButton
              key={snap}
              isActive={bar.position === snap}
              aria-pressed={bar.position === snap}
              onClick={() => bar.setPosition(snap)}
            >
              {label}
            </ToolbarTextButton>
          ))}

          {/* More — toggle the Captions panel */}
          {settings && (
            <>
              <ToolbarDivider />
              <ToolbarIconButton
                aria-label="More options"
                isActive={isSidebarOnCaptions}
                onClick={() => settings.toggle('captions')}
              >
                <EllipsisIcon width="16" height="4" fillColor="currentColor" />
              </ToolbarIconButton>
            </>
          )}
        </FloatingToolbarSurface>
        {styleMenu}
        {colorPickerPopover}
      </ToolbarLayer>
    );
  },
);

// The colour itself, so each button says which colour it holds.
const SSwatch = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid var(--border);
  box-sizing: border-box;
`;

const SStyleButton = styled(ToolbarTextButton)`
  max-width: 140px;
`;

const SLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SStyleMenu = styled(ToolbarMenu)`
  width: 200px;
  max-height: 280px;
  overflow-y: auto;
`;

// The panel's bordered field, compacted to the bar's 28px row.
const SFontPicker = styled(FontFamilyVariantDropdown)`
  width: 150px;
  > button {
    height: 28px;
    padding: 0 8px;
    border-color: transparent;
    font-size: var(--text-sm);
  }
  > button:hover {
    border-color: transparent;
    background: var(--surface-elevated);
  }
`;
